import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryClient';
import { toast } from 'sonner';
import i18n from '@/lib/i18n';

export interface FeatureRequest {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  /** Deutsche Fassung, gepflegt vom Team; NULL bei nutzererzeugten Anfragen. */
  title_de: string | null;
  description_de: string | null;
  category: string;
  status: string;
  vote_count: number;
  is_public: boolean;
  admin_response: string | null;
  admin_response_de: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmitFeatureRequestInput {
  title: string;
  description?: string;
  category: string;
}

export function useFeatureRequests(categoryFilter?: string, statusFilter?: string) {
  return useQuery({
    queryKey: [...queryKeys.featureRequests.all, categoryFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('feature_requests')
        .select('*')
        .eq('is_public', true)
        .order('vote_count', { ascending: false });

      if (categoryFilter && categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }
      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FeatureRequest[];
    },
  });
}

export function useUserVotes() {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.featureRequests.userVotes(user?.id ?? ''),
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('feature_request_votes')
        .select('request_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return data.map((v) => v.request_id);
    },
    enabled: !!user,
  });
}

export function useToggleVote() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase.rpc('toggle_feature_vote', {
        p_request_id: requestId,
        p_user_id: user.id,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.featureRequests.all });
      queryClient.invalidateQueries({ queryKey: ['feature-request-votes'] });
    },
    onError: () => {
      toast.error(i18n.t('roadmap:toasts.voteFailed'));
    },
  });
}

export function useSubmitFeatureRequest() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SubmitFeatureRequestInput) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('feature_requests')
        .insert({
          user_id: user.id,
          title: input.title,
          description: input.description || null,
          category: input.category,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.featureRequests.all });
      toast.success(i18n.t('roadmap:toasts.featureSubmitted'));
    },
    onError: () => {
      toast.error(i18n.t('roadmap:toasts.featureSubmitFailed'));
    },
  });
}
